import { pipe } from '@fxts/core';
import { html, rune, View } from 'rune-ts';
import { LiveDataType } from '../../../../../features/Live/type';
import { makeEmptyScreenNavigate } from '../../../../../shared/app/navigate';
import { lazyLoad } from '../../../../../shared/client/global';
import { staticTypoPcMo, typo } from '../../../../../shared/typography/typo';
import { getYoutubeId, makeYoutubeThumbUrl } from '../../../../../shared/util';
import { ButtonAction } from '../../../../../shared/components/atoms/ButtonAction/ButtonAction';
import { ButtonArrow } from '../../../../components/atoms/ButtonArrow/ButtonArrow';
import { MShopDefaultIcon } from '../../../../components/atoms/Icon';
import { ProductCardListView } from '../../../../components/cells/ProductCardList/ProductCardListView.ts';
import klass from './PlusLive.module.scss';
import { ProductCardSize, ProductCardTheme } from '../../../../../features/ProductList/type';

export type PlusLiveProps = { lives: LiveDataType[] };

class PlusLivePaginationView extends View<{
  current_index: number;
  max_index: number;
}> {
  protected override template() {
    const pagination = this.data;
    const is_mobile = !!rune.getSharedData(this)?.is_mobile;

    return html`<div
      class="pagination ${klass.pagination} ${staticTypoPcMo({
        is_mobile,
        mo: 'bebas_14_bold',
        pc: 'bebas_16_bold',
      })}"
    >
      <span class="current_index"> ${this.calcIndex(pagination.current_index)} </span>
      <div class="${klass.pages}">
        <div class="progress ${klass.page}" style="width: ${this.calcProgress()}%;"></div>
      </div>
      <span> ${this.calcIndex(pagination.max_index)} </span>
    </div>`;
  }

  calcIndex(index: number) {
    return `${index + 1 < 10 ? '0' : ''}${index + 1}`;
  }

  calcProgress() {
    const pagination = this.data;

    return ((pagination.current_index + 1) / (pagination.max_index + 1)) * 100;
  }
}

class PlusLiveBodyView extends View<LiveDataType> {
  constructor(
    data: LiveDataType,
    public show_button: boolean,
    public current_index: number,
    public max_index: number,
  ) {
    super(data);
  }

  override template() {
    const { title, platform, iframe_url, products } = this.data;
    const queryString = iframe_url.includes('?') ? '&' : '?';
    const is_mobile = !!rune.getSharedData(this)?.is_mobile;

    return html`
      <div class="${klass.content_container}">
        <div class="${klass.background}">
          <img
            class="${klass.image}"
            src="${platform === 'youtube' ? this.makeYoutubeThumbnail() : products[0]?.profile_image_url}"
            alt=""
          />
          <div class="${klass.dim}"></div>
        </div>
        <div class="${klass.content}">
          ${is_mobile
            ? html`
                <span class="${klass.body} ${typo('24_bold')}"> ${html.preventEscape(title ?? '')} </span>
              `
            : ''}
          <div class="${klass.live_frame_container}">
            <div class="${klass.live_frame}">
              ${platform === 'youtube'
                ? html` <iframe
                    data-src="${iframe_url}${queryString}mute=1&autoplay=1"
                    class="don_lazy"
                    title="YouTube video player"
                    frameborder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowfullscreen="false"
                  ></iframe>`
                : platform === 'afreecatv'
                  ? html` <iframe
                      data-src="${iframe_url}${queryString}autoplay=true"
                      class="don_lazy"
                      frameborder="0"
                      allowfullscreen="false"
                    ></iframe>`
                  : html` <iframe
                      data-src="${iframe_url}${queryString}muted=true&autoplay=true"
                      class="don_lazy"
                      frameborder="0"
                      allowfullscreen="false"
                      scrolling="no"
                    ></iframe>`}
            </div>
            ${is_mobile ? new PlusLivePaginationView(this) : ''}
          </div>
          <div class="${klass.live_content_container}">
            <div class="${klass.live_content}">
              ${!is_mobile
                ? html`
                    <span class="${klass.body} ${typo('28_bold')}"> ${html.preventEscape(title ?? '')} </span>
                  `
                : ''}
              ${!is_mobile && this.data.shop_url && this.data.label
                ? new ButtonAction({
                    navigate: makeEmptyScreenNavigate(this.data.shop_url),
                    href: this.data.shop_url,
                    target: '_blank',
                    size: 'medium',
                    type: 'blue',
                    label: html`
                      <span class="${klass.button_label}">
                        ${html.preventEscape(this.data.label)}
                        <span class="${klass.button_icon}">${MShopDefaultIcon()}</span>
                      </span>
                    `,
                    fit: true,
                  })
                : ''}
            </div>
            <div class="${klass.bottom}">
              <div class="${klass.container}">
                ${products.length > 0
                  ? new ProductCardListView(
                      { products },
                      {
                        size: ProductCardSize.small,
                        resize_width: 600,
                        is_horizontal: is_mobile,
                        klass: klass.product,
                        container_klass: klass.product_container,
                        theme: is_mobile ? ProductCardTheme.dark : ProductCardTheme.light,
                        hide_badges: is_mobile,
                        hide_tag: is_mobile,
                      },
                    )
                  : ''}
                ${is_mobile && this.data.shop_url && this.data.label
                  ? new ButtonAction({
                      navigate: makeEmptyScreenNavigate(this.data.shop_url),
                      href: this.data.shop_url,
                      target: '_blank',
                      size: 'regular',
                      type: 'blue',
                      label: html`
                        <span class="${klass.button_label}">
                          ${html.preventEscape(this.data.label)}
                          <span class="${klass.button_icon}">${MShopDefaultIcon()}</span>
                        </span>
                      `,
                      fit: true,
                    })
                  : ''}
              </div>

              ${this.show_button
                ? html` <div class="navigation ${klass.navigation}">
                    ${new ButtonArrow({ direction: 'left', type: 'dim' })}
                    ${new ButtonArrow({ direction: 'right', type: 'dim' })}
                  </div>`
                : ''}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  makeYoutubeThumbnail() {
    return pipe(this.data.iframe_url, getYoutubeId, makeYoutubeThumbUrl);
  }

  protected override onRender() {
    this.addEventListener('animationend', () => {
      this.element().dataset.show = '';
      lazyLoad();
    });
  }

  show() {
    this.element().dataset.show = 'true';
  }

  hide() {
    this.element().dataset.hide = 'true';
  }

  remove() {
    this.element().remove();
  }
}

export class PlusLive extends View<PlusLiveProps> {
  active_live: LiveDataType;
  active_index: number;
  animating = false;

  constructor(data: PlusLiveProps) {
    super(data);

    this.active_index = 0;
    this.active_live = data.lives[this.active_index]!;
  }

  override template() {
    return html`<div class="${klass.container}">
      ${new PlusLiveBodyView(
        this.active_live,
        this.data.lives.length > 1,
        this.active_index,
        this.data.lives.length - 1,
      )}
    </div> `;
  }

  protected override onRender() {
    if (this.data.lives.length < 2) {
      return;
    }

    let auto_play = window.setInterval(() => {
      this.change('right');
    }, 15000);

    this.addEventListener('mouseenter', () => {
      clearInterval(auto_play);
    });

    this.addEventListener('mouseleave', () => {
      auto_play = window.setInterval(() => {
        this.change('right');
      }, 15000);
    });

    this.delegate('click', ButtonArrow, (_e, targetView) => {
      this.change(targetView.data.direction ?? 'left');
    });
  }

  change(direction: 'left' | 'right') {
    if (this.animating) return;
    this.animating = true;
    this.changeActiveLive(direction);
    this.changeBody();
  }

  changeActiveLive(direction: 'left' | 'right') {
    const direction_int = direction === 'left' ? -1 : 1;
    const index =
      this.active_index === 0 && direction_int === -1
        ? this.data.lives.length + direction_int
        : this.active_index === this.data.lives.length - 1 && direction_int === 1
          ? 0
          : this.active_index + direction_int;

    this.active_live = this.data.lives[index]!;
    this.active_index = index;
  }

  changeBody() {
    const body = this.subView(PlusLiveBodyView);

    body
      ?.addEventListener('animationend', () => {
        this.animating = false;
        body?.remove();
      })
      ?.hide();

    const new_live = new PlusLiveBodyView(
      this.active_live,
      this.data.lives.length > 1,
      this.active_index,
      this.data.lives.length - 1,
    );
    this.element().appendChild(new_live.render());
    new_live.show();
  }
}
